// Wormhole flythrough — bidirectional Sol ↔ Gargantua transition.
//
// Stargate-style ride. Triggered by proximity to either wormhole
// mouth or by the Star Map. The visual is an inside-facing procedural
// sphere that follows the camera: the fragment shader builds a watery
// deep-blue tunnel (domain-warped FBM walls, energy ripples flowing
// past, a far end that recedes into deep navy haze, S-curve snaking
// with banking that occludes the view ahead) entirely from the
// camera-local ray direction. No UVs, no texture
// projection, no cylinder wrap seam, no dependence on world scale.

import * as THREE from 'three';
import {
  wormholeTunnelVertexShader,
  wormholeTunnelFragmentShader
} from './shaders/wormhole.js';

const TUNNEL_LENGTH = 4000;
const TUNNEL_RADIUS = 220;
const TUNNEL_VISUAL_RADIUS = 1400;
const FLIGHT_DURATION_MS = 6800;

// Event-horizon flash envelope (fractions of flight time): a brief
// engulfing white-cyan flash as the camera hits the puddle, darkness
// for the ride, then a rushing brighten on exit. The post-flight tail
// holds the exit flash over the revealed destination and fades it,
// so arrival reads as bursting out of the gate, not a hard cut.
const ENTRY_FLASH_END = 0.07;
const EXIT_FLASH_START = 0.93;
const EXIT_TAIL_MS = 420;

// Per-frame scratch (no allocations inside the rAF loop).
const _dir = new THREE.Vector3();
const _side = new THREE.Vector3();
const _up = new THREE.Vector3();

function smooth01(x) {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
}

/**
 * @param {Object} ctx
 * @param {THREE.PerspectiveCamera} ctx.camera
 * @param {THREE.Scene} ctx.scene
 * @param {() => void} ctx.onMidpoint
 * @param {() => void} [ctx.onComplete]
 */
export function createWormholeTunnel({ camera, scene, onMidpoint, onComplete }) {
  const geometry = new THREE.IcosahedronGeometry(TUNNEL_VISUAL_RADIUS, 6);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime:   { value: 0 },
      uTravel: { value: 0 },
      uFlash:  { value: 0 },
      uAlpha:  { value: 1 },
    },
    vertexShader: wormholeTunnelVertexShader,
    fragmentShader: wormholeTunnelFragmentShader,
    side: THREE.BackSide,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    toneMapped: false,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'wormholeTunnel';
  mesh.renderOrder = 9999;
  mesh.visible = false;
  mesh.frustumCulled = false;

  let active = false;
  let startTime = 0;
  let tailRaf = 0;
  const segmentStartPos = new THREE.Vector3();
  const segmentStartQuat = new THREE.Quaternion();

  function syncVisualShell() {
    mesh.position.copy(camera.position);
    mesh.quaternion.copy(camera.quaternion);
  }

  function setSegmentStart() {
    segmentStartPos.copy(camera.position);
    segmentStartQuat.copy(camera.quaternion);
    syncVisualShell();
  }

  function teardown() {
    mesh.visible = false;
    if (mesh.parent) mesh.parent.remove(mesh);
    material.uniforms.uAlpha.value = 1;
    material.uniforms.uFlash.value = 0;
  }

  function cancelTail() {
    if (tailRaf) {
      cancelAnimationFrame(tailRaf);
      tailRaf = 0;
      teardown();
    }
  }

  // The options argument (the old { tint }) is accepted for API
  // compatibility but ignored: the SG-1 wormhole interior is the same
  // strict blue in both directions.
  function play() {
    if (active) return Promise.resolve();
    active = true;
    cancelTail();
    startTime = performance.now();

    material.uniforms.uTime.value = 0;
    material.uniforms.uTravel.value = 0;
    material.uniforms.uFlash.value = 1;  // engulfing event-horizon hit
    material.uniforms.uAlpha.value = 1;  // walls opaque for the whole ride

    mesh.visible = true;
    if (!mesh.parent) scene.add(mesh);
    setSegmentStart();

    return new Promise(resolve => {
      let midpointFired = false;

      function step() {
        const now = performance.now();
        const t = Math.min(1, (now - startTime) / FLIGHT_DURATION_MS);
        const segmentT = t < 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
        const easedSegmentT = segmentT * segmentT * segmentT * (segmentT * (segmentT * 6.0 - 15.0) + 10.0);

        material.uniforms.uTime.value = (now - startTime) / 1000;
        material.uniforms.uTravel.value = t;
        // Flash envelope: bright engulf at the puddle, dark mid-ride,
        // rushing white-cyan brighten on exit.
        const entryFlash = 1 - smooth01(t / ENTRY_FLASH_END);
        const exitFlash = smooth01((t - EXIT_FLASH_START) / (1 - EXIT_FLASH_START));
        material.uniforms.uFlash.value = Math.max(entryFlash, exitFlash);

        _dir.set(0, 0, -1).applyQuaternion(segmentStartQuat);
        _side.set(1, 0, 0).applyQuaternion(segmentStartQuat);
        _up.set(0, 1, 0).applyQuaternion(segmentStartQuat);
        const segmentSign = t < 0.5 ? 1 : -1;
        const localZ = TUNNEL_LENGTH * 0.48 * easedSegmentT * segmentSign;
        const arc = Math.sin(t * Math.PI * 2.0) * TUNNEL_RADIUS * 0.12;
        const lift = Math.sin(t * Math.PI) * TUNNEL_RADIUS * 0.05;

        camera.position.copy(segmentStartPos)
          .addScaledVector(_dir, localZ)
          .addScaledVector(_side, arc)
          .addScaledVector(_up, lift);
        syncVisualShell();

        if (t >= 0.5 && !midpointFired) {
          midpointFired = true;
          try {
            if (typeof onMidpoint === 'function') onMidpoint();
          } catch (err) {
            console.error('[wormhole-tunnel] midpoint error', err);
          }
          setSegmentStart();
        }

        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          active = false;
          try {
            if (typeof onComplete === 'function') onComplete();
          } catch (err) {
            console.error('[wormhole-tunnel] complete error', err);
          }
          // Exit tail: keep the shell at full flash and fade its
          // opacity over the destination system.
          const tailStart = now;
          function tailStep() {
            const k = Math.min(1, (performance.now() - tailStart) / EXIT_TAIL_MS);
            material.uniforms.uFlash.value = 1;
            material.uniforms.uAlpha.value = 1 - smooth01(k);
            syncVisualShell();
            if (k < 1) {
              tailRaf = requestAnimationFrame(tailStep);
            } else {
              tailRaf = 0;
              teardown();
            }
          }
          tailRaf = requestAnimationFrame(tailStep);
          resolve();
        }
      }

      requestAnimationFrame(step);
    });
  }

  function dispose() {
    cancelTail();
    geometry.dispose();
    material.dispose();
    if (mesh.parent) mesh.parent.remove(mesh);
  }

  return {
    mesh,
    play,
    dispose,
    isActive: () => active,
  };
}
